import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BodyBinding,
  HttpMethod,
  HttpTool,
  InputType,
  NamedBinding,
  ResponseSave,
  ScenarioConfig,
  ScenarioIssue,
  SuccessRule,
  SystemToolMeta,
} from './types';
import {
  INPUT_TYPES,
  isSystemTool,
  isTemporalType,
  moveItem,
  narrowInputs,
  newInput,
  newResponseSave,
  newSuccessRule,
  omitKeys,
  removeAt,
  renameInputAt,
  renameToolAt,
  setIn,
  withMaxFailedAttempts,
  withNarrowBy,
  withNarrowTruncated,
  withOperation,
  withPath,
  withSaveFact,
  withSaveFormat,
  withSaveMode,
} from './scenarioDraft';
import { RequirementsEditor } from './RequirementsEditor';
import { SystemToolEditor } from './SystemToolEditor';
import { hasAgentFlag } from './systemTools';
import { JsonLiteralField, ValueSourceEditor } from './ValueSourceEditor';
import {
  AddButton,
  FieldLabel,
  IssueList,
  ItemRow,
  ListTextField,
  MultiSelectField,
  NameField,
  RowActions,
  Section,
  SelectField,
  TextField,
  Toggle,
} from './ui';

const METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

interface Props {
  config: ScenarioConfig;
  index: number;
  onChange: (config: ScenarioConfig) => void;
  issues: ScenarioIssue[];
  systemTools?: SystemToolMeta[];
  /** Ids of system tools enabled in the agent settings; undefined when unknown. */
  enabledSystemTools?: string[];
}

export const ToolEditor: React.FC<Props> = ({ systemTools = [], enabledSystemTools, ...props }) => {
  const tool = props.config.tools[props.index];
  if (!isSystemTool(tool)) return <HttpToolEditor {...props} />;
  return (
    <SystemToolEditor
      {...props}
      systemTools={systemTools}
      agentToolEnabled={
        !enabledSystemTools || !hasAgentFlag(tool.system) || enabledSystemTools.includes(tool.system)
      }
    />
  );
};

const HttpToolEditor: React.FC<Props> = ({ config, index, onChange, issues }) => {
  const { t } = useTranslation();
  const tool = config.tools[index] as HttpTool;
  const ptr = `/tools/${index}`;
  const updateTool = (next: HttpTool) => onChange(setIn(config, ['tools', index], next));
  const update = (path: (string | number)[], value: unknown) => updateTool(setIn(tool, path, value));
  const factOptions = config.facts.map(f => ({ value: f.name, label: `${f.name} (${f.type})` }));
  const apiFactOptions = config.facts
    .filter(f => f.source === 'api')
    .map(f => ({ value: f.name, label: `${f.name} (${f.type})` }));
  const request = tool.request;
  const response = tool.response;

  const bindingRows = (
    key: 'query' | 'body',
    items: (NamedBinding | BodyBinding)[],
    nameKey: 'name' | 'pointer'
  ) =>
    items.map((item, itemIndex) => {
      const itemPtr = `${ptr}/request/${key}/${itemIndex}`;
      const label = nameKey === 'name' ? t('agents.scenario.queryName') : t('agents.scenario.bodyPointer');
      return (
        <ItemRow key={itemIndex} className="grid grid-cols-1 gap-2 md:grid-cols-[200px_1fr_auto]">
          <TextField
            label={label}
            value={(item as unknown as Record<string, string>)[nameKey]}
            onChange={value => update(['request', key, itemIndex, nameKey], value)}
            issues={issues}
            pointer={`${itemPtr}/${nameKey}`}
            monospace
          />
          <ValueSourceEditor
            config={config}
            tool={tool}
            value={item.value}
            onChange={value => update(['request', key, itemIndex, 'value'], value)}
            issues={issues}
            pointer={`${itemPtr}/value`}
            allowLists={key === 'body'}
          />
          <div className="self-end pb-1">
            <RowActions
              index={itemIndex}
              count={items.length}
              onMove={(from, to) => update(['request', key], moveItem(items, from, to))}
              onRemove={() => update(['request', key], removeAt(items, itemIndex))}
            />
          </div>
        </ItemRow>
      );
    });

  const successRule = response.success_rule;
  const narrowBy = successRule.type === 'exactly_one' ? successRule.narrow_by : undefined;
  const narrowPath = ['response', 'success_rule', 'narrow_by'];
  const narrowPtr = `${ptr}/response/success_rule/narrow_by`;

  return (
    <div className="space-y-3">
      <IssueList issues={issues} pointer={ptr} />

      <Section title={t('agents.scenario.sectionGeneral')}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <NameField
            label={t('agents.scenario.technicalName')}
            value={tool.id}
            onCommit={value => onChange(renameToolAt(config, index, value))}
            issues={issues}
            pointer={`${ptr}/id`}
          />
          <TextField
            label={t('agents.scenario.toolTitle')}
            value={tool.title}
            onChange={value => update(['title'], value)}
            issues={issues}
            pointer={`${ptr}/title`}
          />
          <SelectField
            label={t('agents.scenario.operation')}
            value={tool.operation}
            options={[
              { value: 'read', label: t('agents.scenario.operationRead') },
              { value: 'write', label: t('agents.scenario.operationWrite') },
            ]}
            onChange={value => updateTool(withOperation(tool, value as HttpTool['operation']))}
            issues={issues}
            pointer={`${ptr}/operation`}
          />
        </div>
        <TextField
          label={t('agents.scenario.descriptionForAi')}
          value={tool.description}
          onChange={value => update(['description'], value)}
          issues={issues}
          pointer={`${ptr}/description`}
          multiline
        />
        <Toggle label={t('agents.scenario.toolEnabled')} checked={tool.enabled} onChange={value => update(['enabled'], value)} />
        <div className="max-w-xs">
          <TextField
            label={t('agents.scenario.maxFailedAttempts')}
            hint={t('agents.scenario.maxFailedAttemptsHint')}
            type="number"
            value={tool.max_failed_attempts?.toString() ?? ''}
            onChange={value => updateTool(withMaxFailedAttempts(tool, value))}
            issues={issues}
            pointer={`${ptr}/max_failed_attempts`}
          />
        </div>
      </Section>

      <Section
        title={t('agents.scenario.sectionInputs')}
        subtitle={t('agents.scenario.sectionInputsHint')}
        issues={issues}
        pointer={`${ptr}/inputs`}
      >
        {tool.inputs.map((input, inputIndex) => {
          const inputPtr = `${ptr}/inputs/${inputIndex}`;
          return (
            <ItemRow key={inputIndex} className="grid grid-cols-1 gap-2 md:grid-cols-[180px_130px_1fr_auto_auto]">
              <NameField
                label={t('agents.scenario.technicalName')}
                value={input.name}
                onCommit={value => updateTool(renameInputAt(tool, inputIndex, value))}
                issues={issues}
                pointer={`${inputPtr}/name`}
              />
              <SelectField
                label={t('agents.scenario.type')}
                value={input.type}
                options={INPUT_TYPES.map(type => ({ value: type, label: type }))}
                onChange={value => update(['inputs', inputIndex, 'type'], value as InputType)}
                issues={issues}
                pointer={`${inputPtr}/type`}
              />
              <TextField
                label={t('agents.scenario.descriptionForAi')}
                value={input.description}
                onChange={value => update(['inputs', inputIndex, 'description'], value)}
                issues={issues}
                pointer={`${inputPtr}/description`}
              />
              <div className="self-end pb-2">
                <Toggle
                  label={t('agents.scenario.required')}
                  checked={input.required}
                  onChange={value => update(['inputs', inputIndex, 'required'], value)}
                />
              </div>
              <div className="self-end pb-1">
                <RowActions
                  index={inputIndex}
                  count={tool.inputs.length}
                  onMove={(from, to) => update(['inputs'], moveItem(tool.inputs, from, to))}
                  onRemove={() => update(['inputs'], removeAt(tool.inputs, inputIndex))}
                />
              </div>
            </ItemRow>
          );
        })}
        <AddButton label={t('agents.scenario.addInput')} onClick={() => update(['inputs'], [...tool.inputs, newInput(tool)])} />
      </Section>

      <Section title={t('agents.scenario.sectionRequirements')} issues={issues} pointer={`${ptr}/requires`}>
        <RequirementsEditor
          config={config}
          tool={tool}
          pointer={`${ptr}/requires`}
          onChange={requires => update(['requires'], requires)}
          issues={issues}
        />
      </Section>

      <Section title={t('agents.scenario.sectionRequest')} issues={issues} pointer={`${ptr}/request`}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[180px_120px_1fr]">
          <SelectField
            label={t('agents.scenario.connection')}
            value={request.connection}
            options={config.connections.map(c => ({ value: c.id, label: c.id }))}
            onChange={value => update(['request', 'connection'], value)}
            issues={issues}
            pointer={`${ptr}/request/connection`}
          />
          <SelectField
            label={t('agents.scenario.method')}
            value={request.method}
            options={METHODS.map(method => ({ value: method, label: method }))}
            onChange={value => update(['request', 'method'], value)}
            issues={issues}
            pointer={`${ptr}/request/method`}
          />
          <TextField
            label={t('agents.scenario.path')}
            hint={t('agents.scenario.pathHint')}
            value={request.path}
            onChange={value => update(['request'], withPath(request, value))}
            issues={issues}
            pointer={`${ptr}/request/path`}
            monospace
          />
        </div>

        {request.path_params.length > 0 && (
          <div className="space-y-2">
            <FieldLabel label={t('agents.scenario.pathParams')} />
            <IssueList issues={issues} pointer={`${ptr}/request/path_params`} />
            {request.path_params.map((param, paramIndex) => (
              <div key={param.name} className="grid grid-cols-1 gap-2 md:grid-cols-[200px_1fr]">
                <div className="self-center font-mono text-sm text-gray-700 dark:text-gray-300">{`{${param.name}}`}</div>
                <ValueSourceEditor
                  config={config}
                  tool={tool}
                  value={param.value}
                  onChange={value => update(['request', 'path_params', paramIndex, 'value'], value)}
                  issues={issues}
                  pointer={`${ptr}/request/path_params/${paramIndex}/value`}
                  allowLists={false}
                />
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel label={t('agents.scenario.queryParams')} />
          {bindingRows('query', request.query, 'name')}
          <AddButton
            label={t('agents.scenario.addQueryParam')}
            onClick={() =>
              update(['request', 'query'], [...request.query, { name: '', value: { source: 'fact', key: '' } }])
            }
          />
        </div>

        <div className="space-y-2">
          <FieldLabel label={t('agents.scenario.bodyFields')} hint={t('agents.scenario.bodyFieldsHint')} />
          <IssueList issues={issues} pointer={`${ptr}/request/body`} />
          {bindingRows('body', request.body, 'pointer')}
          <AddButton
            label={t('agents.scenario.addBodyField')}
            onClick={() =>
              update(['request', 'body'], [...request.body, { pointer: '/', value: { source: 'fact', key: '' } }])
            }
          />
        </div>
      </Section>

      <Section title={t('agents.scenario.sectionResponse')} issues={issues} pointer={`${ptr}/response`}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[200px_200px_1fr_1fr]">
          <ListTextField
            label={t('agents.scenario.successStatus')}
            hint={t('agents.scenario.commaSeparated')}
            values={response.success_status}
            onCommit={values =>
              update(
                ['response', 'success_status'],
                values.map(value => Number.parseInt(value, 10)).filter(Number.isFinite)
              )
            }
            issues={issues}
            pointer={`${ptr}/response/success_status`}
          />
          <SelectField
            label={t('agents.scenario.successRule')}
            value={successRule.type}
            options={(['http_status', 'exactly_one', 'field_equals'] as const).map(type => ({
              value: type,
              label: t(`agents.scenario.successRules.${type}`),
            }))}
            onChange={value =>
              update(['response', 'success_rule'], newSuccessRule(value as SuccessRule['type'], successRule))
            }
            issues={issues}
            pointer={`${ptr}/response/success_rule/type`}
          />
          {successRule.type !== 'http_status' && (
            <TextField
              label={t('agents.scenario.responsePointer')}
              value={successRule.pointer}
              onChange={value => update(['response', 'success_rule', 'pointer'], value)}
              issues={issues}
              pointer={`${ptr}/response/success_rule/pointer`}
              monospace
            />
          )}
          {successRule.type === 'field_equals' && (
            <div>
              <FieldLabel label={t('agents.scenario.expectedValue')} hint={t('agents.scenario.jsonLiteralHint')} />
              <JsonLiteralField
                value={successRule.value}
                onChange={value => update(['response', 'success_rule', 'value'], value)}
                invalidLabel={t('agents.scenario.invalidJsonLiteral')}
              />
              <IssueList issues={issues} pointer={`${ptr}/response/success_rule/value`} />
            </div>
          )}
        </div>
        <IssueList issues={issues} pointer={`${ptr}/response/success_rule`} />

        {successRule.type === 'exactly_one' && (
          <div className="space-y-2">
            <Toggle
              label={t('agents.scenario.narrowBy.enabled')}
              checked={narrowBy !== undefined}
              onChange={value => updateTool(withNarrowBy(tool, value))}
            />
            {narrowBy && (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.narrowBy.hint')}</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[200px_1fr_1fr]">
                  <SelectField
                    label={t('agents.scenario.narrowBy.input')}
                    hint={t('agents.scenario.narrowBy.inputHint')}
                    value={narrowBy.input}
                    options={narrowInputs(tool).map(input => ({ value: input.name, label: input.name }))}
                    onChange={value => update([...narrowPath, 'input'], value)}
                    issues={issues}
                    pointer={`${narrowPtr}/input`}
                    placeholder={t('agents.scenario.selectPlaceholder')}
                  />
                  <ListTextField
                    label={t('agents.scenario.narrowBy.fields')}
                    hint={t('agents.scenario.narrowBy.fieldsHint')}
                    values={narrowBy.fields}
                    onCommit={values => update([...narrowPath, 'fields'], values)}
                    issues={issues}
                    pointer={`${narrowPtr}/fields`}
                  />
                  <TextField
                    label={t('agents.scenario.narrowBy.truncated')}
                    hint={t('agents.scenario.narrowBy.truncatedHint')}
                    placeholder="/hasNext"
                    value={narrowBy.truncated ?? ''}
                    onChange={value => update(narrowPath, withNarrowTruncated(narrowBy, value))}
                    issues={issues}
                    pointer={`${narrowPtr}/truncated`}
                    monospace
                  />
                </div>
                {narrowBy.fields.map((_, fieldIndex) => (
                  <IssueList key={fieldIndex} issues={issues} pointer={`${narrowPtr}/fields/${fieldIndex}`} />
                ))}
                <IssueList issues={issues} pointer={narrowPtr} />
              </>
            )}
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel label={t('agents.scenario.saveToFacts')} />
          {response.save.map((save, saveIndex) => {
            const savePtr = `${ptr}/response/save/${saveIndex}`;
            const targetFact = config.facts.find(f => f.name === save.fact);
            const showFormat =
              save.mode !== 'append_unique' && (isTemporalType(targetFact?.type) || save.format !== undefined);
            return (
              <ItemRow key={saveIndex} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_160px_160px_auto_auto]">
                <TextField
                  label={t('agents.scenario.responsePointer')}
                  value={save.pointer}
                  onChange={value => update(['response', 'save', saveIndex, 'pointer'], value)}
                  issues={issues}
                  pointer={`${savePtr}/pointer`}
                  monospace
                />
                <SelectField
                  label={t('agents.scenario.fact')}
                  value={save.fact}
                  options={apiFactOptions}
                  onChange={value => update(['response', 'save', saveIndex], withSaveFact(save, value, config.facts))}
                  issues={issues}
                  pointer={`${savePtr}/fact`}
                />
                <SelectField
                  label={t('agents.scenario.saveMode')}
                  value={save.mode}
                  options={[
                    { value: 'replace', label: t('agents.scenario.saveModes.replace') },
                    { value: 'append_unique', label: t('agents.scenario.saveModes.append_unique') },
                  ]}
                  onChange={value =>
                    update(['response', 'save', saveIndex], withSaveMode(save, value as ResponseSave['mode']))
                  }
                  issues={issues}
                  pointer={`${savePtr}/mode`}
                />
                {save.mode === 'append_unique' ? (
                  <SelectField
                    label={t('agents.scenario.uniqueKey')}
                    value={save.unique_key ?? ''}
                    options={(targetFact?.item_fields ?? []).map(f => ({ value: f.name, label: f.name }))}
                    onChange={value => update(['response', 'save', saveIndex, 'unique_key'], value)}
                    issues={issues}
                    pointer={`${savePtr}/unique_key`}
                  />
                ) : showFormat ? (
                  <TextField
                    label={t('agents.scenario.sourceFormat')}
                    placeholder="ISO"
                    value={save.format ?? ''}
                    onChange={value => update(['response', 'save', saveIndex], withSaveFormat(save, value))}
                    issues={issues}
                    pointer={`${savePtr}/format`}
                    monospace
                  />
                ) : (
                  <div />
                )}
                <div className="self-end pb-2">
                  <Toggle
                    label={t('agents.scenario.required')}
                    checked={save.required}
                    onChange={value => update(['response', 'save', saveIndex, 'required'], value)}
                  />
                </div>
                <div className="self-end pb-1">
                  <RowActions
                    index={saveIndex}
                    count={response.save.length}
                    onMove={(from, to) => update(['response', 'save'], moveItem(response.save, from, to))}
                    onRemove={() => update(['response', 'save'], removeAt(response.save, saveIndex))}
                  />
                </div>
                <div className="md:col-span-6">
                  {showFormat && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.sourceFormatHint')}</p>
                  )}
                  {!showFormat && <IssueList issues={issues} pointer={`${savePtr}/format`} />}
                  <IssueList issues={issues} pointer={savePtr} />
                </div>
              </ItemRow>
            );
          })}
          <AddButton
            label={t('agents.scenario.addSave')}
            onClick={() => update(['response', 'save'], [...response.save, newResponseSave()])}
          />
        </div>

        <TextField
          label={t('agents.scenario.failureHint')}
          hint={t('agents.scenario.failureHintHint')}
          value={response.failure_hint ?? ''}
          onChange={value => {
            const rest = omitKeys(response, 'failure_hint');
            update(['response'], value ? { ...rest, failure_hint: value } : rest);
          }}
          issues={issues}
          pointer={`${ptr}/response/failure_hint`}
          multiline
        />
        <TextField
          label={t('agents.scenario.errorPointer')}
          hint={t('agents.scenario.errorPointerHint')}
          value={response.error_pointer ?? ''}
          onChange={value => {
            const rest = omitKeys(response, 'error_pointer');
            update(['response'], value ? { ...rest, error_pointer: value } : rest);
          }}
          issues={issues}
          pointer={`${ptr}/response/error_pointer`}
        />
      </Section>

      {tool.operation === 'write' && tool.confirmation && (
        <Section title={t('agents.scenario.sectionConfirmation')} issues={issues} pointer={`${ptr}/confirmation`}>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('agents.scenario.confirmationHint')}</p>
          <MultiSelectField
            label={t('agents.scenario.reviewFields')}
            values={tool.confirmation.review_fields}
            options={factOptions}
            onChange={values => update(['confirmation', 'review_fields'], values)}
            issues={issues}
            pointer={`${ptr}/confirmation/review_fields`}
          />
          <TextField
            label={t('agents.scenario.confirmationQuestion')}
            value={tool.confirmation.question}
            onChange={value => update(['confirmation', 'question'], value)}
            issues={issues}
            pointer={`${ptr}/confirmation/question`}
          />
          <IssueList issues={issues} pointer={`${ptr}/confirmation`} />
        </Section>
      )}
    </div>
  );
};
